/**
 * Massachusetts State-Specific Diagnostic Questions
 * 
 * CRITICAL REGULATORY FRAMEWORK:
 * Massachusetts does NOT have state licensure for home health agencies.
 * From Mass.gov: "the Department of Public Health does not license home health agencies.
 * A home health agency may become certified to take part in the Medicare and Medicaid programs."
 * 
 * Applicable Regulations:
 * - 42 CFR Part 484: Federal Medicare Conditions of Participation (for Medicare/Medicaid certification)
 * - 105 CMR 155.000: Patient and Resident Abuse Prevention, Reporting, Investigation, Penalties and Registry
 * - M.G.L. c. 111, §§ 72F-72L: Patient Abuse Statute
 * - M.G.L. c. 6, § 172: Criminal Offender Record Information (CORI)
 * - M.G.L. c. 6, § 178K: Sex Offender Registry Information (SORI)
 * - M.G.L. c. 119, § 51A: Mandated Reporter - Child Abuse
 * - M.G.L. c. 19A, § 15: Mandated Reporter - Elder Abuse
 * - M.G.L. c. 152: Workers' Compensation Act
 * 
 * Key Agencies:
 * - DPH Division of Health Care Facility Licensure and Certification: (617) 753-8000
 * - DPH Patient Abuse Hotline: (800) 462-5540
 * - DCJIS (CORI): (617) 660-4600
 * - MassHealth Provider Enrollment: (800) 841-2900
 * - Elder Abuse Hotline: (800) 922-2275
 * - DCF Child Abuse Hotline: (800) 792-5200
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const MASSACHUSETTS_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: CERTIFICATION & OPERATIONS
  // ============================================
  {
    id: 'MA-A1',
    itemId: 'A1',
    stateCode: 'MA',
    title: 'Medicare/Medicaid Certification Status',
    description: 'Massachusetts does not license home health agencies at the state level. Agencies providing skilled services must obtain Medicare certification through CMS under 42 CFR Part 484.',
    severity: 'high',
    regulationCode: '42-484',
    regulationName: 'Certification',
    agencyName: 'Centers for Medicare & Medicaid Services / Massachusetts DPH',
    agencyAbbr: 'CMS/DPH',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.mass.gov/how-to/apply-for-home-health-agency-medicare-certification',
    questions: [
      {
        id: 'q1',
        question: 'What is your agency\'s certification status?',
        inputType: 'select',
        options: [
          { value: 'medicare_medicaid', label: 'Medicare and Medicaid certified' },
          { value: 'medicare_only', label: 'Medicare certified only' },
          { value: 'pending', label: 'Certification pending' },
          { value: 'not_certified', label: 'Not certified (private pay only)' },
        ],
        required: true,
        helpText: 'Medicare certification is required to bill Medicare/Medicaid. Private pay agencies do not require state licensure in Massachusetts.',
      },
      {
        id: 'q2',
        question: 'Do you have deemed status from a CMS-approved accrediting organization?',
        inputType: 'select',
        options: [
          { value: 'joint_commission', label: 'Yes - The Joint Commission' },
          { value: 'chap', label: 'Yes - CHAP' },
          { value: 'achc', label: 'Yes - ACHC' },
          { value: 'state_survey', label: 'No - State survey agency' },
          { value: 'not_applicable', label: 'Not applicable (not seeking certification)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'not_certified',
        },
      },
      {
        id: 'q3',
        question: 'When does your current certification/accreditation expire?',
        inputType: 'date',
        helpText: 'Found on your deemed status letter or CMS certification',
        showCondition: {
          questionId: 'q1',
          operator: 'includes',
          value: ['medicare_medicaid', 'medicare_only'],
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'not_certified', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q3', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency\'s certification status is appropriate for services provided.',
      atRisk: 'Your certification expires within 120 days. Contact your accrediting organization or DPH at (617) 753-8000 to begin the renewal process.',
      nonCompliant: 'Your certification has expired or you are providing Medicare/Medicaid services without proper certification. Contact DPH Division of Health Care Facility Licensure at (617) 753-8000 immediately.',
      unknown: 'Verify your certification status with DPH at (617) 753-8000.',
    },
    stateNotes: [
      'Massachusetts does NOT license home health agencies at the state level',
      'Medicare/Medicaid certification is optional but required to bill these programs',
      'Private pay agencies do not require state licensure',
      'Deemed status from JCAHO, CHAP, or ACHC satisfies certification survey requirements',
      'MassHealth enrollment requires Medicare certification first - call (800) 841-2900',
    ],
  },

  {
    id: 'MA-A2',
    itemId: 'A2',
    stateCode: 'MA',
    title: 'Patient Abuse Reporting Compliance (105 CMR 155.000)',
    description: 'Massachusetts requires all home health agencies to comply with patient abuse reporting requirements under 105 CMR 155.000 and M.G.L. c. 111, §§ 72F-72L',
    severity: 'high',
    regulationCode: '105-155.000',
    regulationName: 'Abuse Reporting',
    agencyName: 'Massachusetts Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(800) 462-5540',
    agencyUrl: 'https://www.mass.gov/regulations/105-CMR-15500-patient-and-resident-abuse-prevention-reporting-investigation-penalties-and-registry',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy for reporting suspected patient abuse, neglect, mistreatment, or misappropriation of property?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, fully documented policy in place' },
          { value: 'informal', label: 'Yes, but informal/not fully documented' },
          { value: 'no', label: 'No policy in place' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the DPH Patient Abuse Hotline number (800) 462-5540 posted in a conspicuous location accessible to staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '105 CMR 155.000 requires posting notice with reporting procedures',
      },
      {
        id: 'q3',
        question: 'Do all staff receive training on recognizing and reporting abuse, neglect, and mistreatment?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff receive documented training' },
          { value: 'most', label: 'Most staff, some gaps exist' },
          { value: 'no', label: 'No formal training program' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'informal' },
        { condition: 'equals', questionId: 'q3', value: 'most' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient abuse reporting procedures meet 105 CMR 155.000 requirements.',
      atRisk: 'Some elements of your abuse reporting program need attention. Ensure the DPH hotline (800) 462-5540 is posted and all staff are trained.',
      nonCompliant: 'No patient abuse reporting policy is in place. This violates M.G.L. c. 111, § 72G. Implement a policy immediately and post the DPH Patient Abuse Hotline: (800) 462-5540.',
      unknown: 'Verify your abuse reporting procedures comply with 105 CMR 155.000.',
    },
    stateNotes: [
      'Mandatory reporters must immediately make oral report to supervisor/employer',
      'Employer must immediately notify DPH at (800) 462-5540',
      'Written report required within 48 hours',
      'Fax reports to (617) 753-8165 fulfill both oral and written requirements',
      'Registry maintained for findings against nurse aides, home health aides, and homemakers',
    ],
  },

  {
    id: 'MA-A3',
    itemId: 'A3',
    stateCode: 'MA',
    title: 'Quality Assurance Program (42 CFR 484.65)',
    description: 'Medicare-certified agencies must maintain a Quality Assessment and Performance Improvement (QAPI) program',
    severity: 'high',
    regulationCode: '42-484.65',
    regulationName: 'QAPI',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a formal Quality Assessment and Performance Improvement (QAPI) program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which QAPI components does your agency have in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'data_collection', label: 'Systematic data collection and analysis' },
          { value: 'performance_indicators', label: 'Performance improvement indicators' },
          { value: 'outcome_measures', label: 'Patient outcome measures' },
          { value: 'action_plans', label: 'Performance improvement action plans' },
          { value: 'governing_body', label: 'Governing body oversight and approval' },
          { value: 'infection_surveillance', label: 'Infection control surveillance' },
          { value: 'adverse_events', label: 'Adverse event tracking and analysis' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets 42 CFR 484.65 requirements.',
      atRisk: 'QAPI program is missing some required components. Review 42 CFR 484.65 for all required elements.',
      nonCompliant: 'No QAPI program in place. Medicare-certified agencies must maintain a QAPI program per 42 CFR 484.65.',
      unknown: 'Verify QAPI program meets federal requirements.',
    },
    stateNotes: [
      'QAPI is required for Medicare-certified agencies per 42 CFR 484.65',
      'Program must be approved by governing body per 105 CMR 155.000 agency requirements',
      'Must address all services provided by the agency including abuse prevention',
      'Performance improvement projects must be documented per M.G.L. c.111 quality standards',
      'Data-driven analysis required to identify opportunities for improvement',
    ],
  },

  {
    id: 'MA-A4',
    itemId: 'A4',
    stateCode: 'MA',
    title: 'Emergency Preparedness (42 CFR 484.102)',
    description: 'Medicare-certified agencies must have an emergency preparedness program',
    severity: 'medium',
    regulationCode: '42-484.102',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive written plan reviewed annually' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which emergency preparedness elements does your plan include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'risk_assessment', label: 'Risk assessment based on hazards likely in area' },
          { value: 'policies', label: 'Emergency policies and procedures' },
          { value: 'communication', label: 'Communication plan with contact information' },
          { value: 'patient_tracking', label: 'Patient tracking system during emergencies' },
          { value: 'training', label: 'Staff training and testing program' },
          { value: 'coordination', label: 'Coordination with local emergency management' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'no',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'comprehensive' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness program meets 42 CFR 484.102 requirements.',
      atRisk: 'Emergency preparedness plan is incomplete. Review 42 CFR 484.102 for required elements.',
      nonCompliant: 'No emergency preparedness plan. Medicare-certified agencies must maintain an emergency preparedness program per 42 CFR 484.102.',
      unknown: 'Review emergency preparedness program against federal requirements.',
    },
    stateNotes: [
      'Emergency preparedness required for Medicare-certified agencies per 42 CFR 484.102',
      'Plan must be reviewed and updated annually per 105 CMR 155.000',
      'Coordination with Massachusetts Emergency Management Agency (MEMA) recommended',
      'Patient tracking system must be in place per M.G.L. c.111 continuity of care requirements',
      'Staff training and testing program required',
    ],
  },

  {
    id: 'MA-A5',
    itemId: 'A5',
    stateCode: 'MA',
    title: 'Policies and Procedures (42 CFR 484.50)',
    description: 'Home health agencies must maintain written policies governing services',
    severity: 'medium',
    regulationCode: '42-484.50',
    regulationName: 'Policies',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights and responsibilities' },
          { value: 'admission_discharge', label: 'Admission, transfer, and discharge policies' },
          { value: 'personnel', label: 'Personnel policies and job descriptions' },
          { value: 'supervision', label: 'Clinical supervision procedures' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'abuse_reporting', label: 'Abuse reporting procedures (105 CMR 155.000)' },
          { value: 'infection_control', label: 'Infection control program' },
          { value: 'emergency', label: 'Emergency preparedness plan' },
          { value: 'quality', label: 'Quality improvement program' },
          { value: 'privacy', label: 'HIPAA privacy and security policies' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets required standards.',
      atRisk: 'Your policy manual is missing some required sections. Review 42 CFR Part 484 for Medicare CoP requirements and 105 CMR 155.000 for Massachusetts abuse reporting.',
      nonCompliant: 'Multiple required policies are missing. Immediately develop written policies for all required areas.',
      unknown: 'Review your policies against 42 CFR Part 484 requirements.',
    },
    stateNotes: [
      'Abuse reporting policies must comply with 105 CMR 155.000',
      'Personnel policies must address CORI requirements per M.G.L. c. 6, Section 172',
      'Patient rights policies required per M.G.L. c.111 Section 70E',
      'Infection control policies must meet 42 CFR 484.70 standards',
      'Annual policy review and updates recommended',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'MA-B1',
    itemId: 'B1',
    stateCode: 'MA',
    title: 'CORI Background Checks (M.G.L. c. 6, § 172)',
    description: 'Massachusetts requires Criminal Offender Record Information (CORI) checks for employees with access to patients',
    severity: 'high',
    regulationCode: '6-172',
    regulationName: 'CORI',
    agencyName: 'Massachusetts Department of Criminal Justice Information Services',
    agencyAbbr: 'DCJIS',
    agencyPhone: '(617) 660-4600',
    agencyUrl: 'https://www.mass.gov/orgs/department-of-criminal-justice-information-services',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed direct care staff, which of the following do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'cori', label: 'CORI (Criminal Offender Record Information) check' },
          { value: 'cori_acknowledgment', label: 'Signed CORI Acknowledgement Form' },
          { value: 'cori_policy', label: 'Written CORI policy per M.G.L. c. 6, § 171A' },
          { value: 'sori', label: 'SORI (Sex Offender Registry Information) check' },
          { value: 'oig', label: 'OIG/SAM Exclusion List verification' },
        ],
        required: true,
        helpText: 'CORI checks require signed acknowledgement forms valid for one year',
      },
      {
        id: 'q2',
        question: 'Do any employees begin working with patients BEFORE their CORI check is completed?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'How often do you renew CORI checks?',
        inputType: 'select',
        options: [
          { value: 'annual', label: 'Annually' },
          { value: 'every_3', label: 'Every 3 years' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'unsure', label: 'Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'unsure', result: 'AT_RISK' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'CORI background check documentation is complete per M.G.L. c. 6, § 172.',
      atRisk: 'Background check documentation may be incomplete. CORI checks must be completed before patient contact.',
      nonCompliant: 'Staff are working without proper CORI clearance or are starting work before clearance is received. Employees working before background check clearance is a direct violation of M.G.L. c. 6, § 172. Contact DCJIS at (617) 660-4600.',
      unknown: 'Audit all personnel files for CORI documentation per M.G.L. c. 6, § 172.',
    },
    stateNotes: [
      'CORI Acknowledgement Form required and valid for one year',
      'Employer must have written CORI policy per M.G.L. c. 6, § 171A',
      'Must verify identity with government-issued photo ID',
      'DCJIS maintains log of all CORI requests',
      'Individuals may request self-audit of their CORI history',
    ],
  },

  {
    id: 'MA-B2',
    itemId: 'B2',
    stateCode: 'MA',
    title: 'Sex Offender Registry Check (M.G.L. c. 6, § 178K)',
    description: 'Massachusetts requires Sex Offender Registry Information (SORI) checks',
    severity: 'high',
    regulationCode: '6-178K',
    regulationName: 'SORI',
    agencyName: 'Massachusetts Sex Offender Registry Board',
    agencyAbbr: 'SORB',
    agencyPhone: '(978) 740-6400',
    agencyUrl: 'https://www.mass.gov/orgs/sex-offender-registry-board',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct Sex Offender Registry checks for ALL staff with patient contact?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, for all staff before patient contact' },
          { value: 'some', label: 'For some staff only' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Sex Offender Registry checks are conducted for all staff per M.G.L. c. 6, § 178K.',
      atRisk: 'SORI checks are not conducted for all staff. Implement consistent screening.',
      nonCompliant: 'SORI checks are not being conducted. Contact SORB at (978) 740-6400.',
      unknown: 'Verify SORI check procedures are in place.',
    },
    stateNotes: [
      'SORI checks authorized per M.G.L. c. 6, Section 178K',
      'Sex Offender Registry Board (SORB) maintains registry per M.G.L. c. 6, Section 178D',
      'Check at hire for all staff with patient contact per 105 CMR 155.000',
      'Level 2 and Level 3 offender information is publicly available',
      'SORB phone: (978) 740-6400',
    ],
  },

  {
    id: 'MA-B3',
    itemId: 'B3',
    stateCode: 'MA',
    title: 'OIG/SAM Exclusion Check (42 CFR 1001)',
    description: 'Federal exclusion list verification required for Medicare/Medicaid providers',
    severity: 'high',
    regulationCode: '42-1001',
    regulationName: 'LEIE',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check OIG/SAM exclusion lists for all staff?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion checks are conducted monthly as recommended.',
      atRisk: 'OIG recommends monthly verification, not just at hire. Implement ongoing screening.',
      nonCompliant: 'OIG/SAM exclusion checks are not being conducted. This creates significant liability for Medicare/Medicaid providers.',
      unknown: 'Verify OIG/SAM procedures are in place.',
    },
    stateNotes: [
      'OIG LEIE checks required for Medicare/Medicaid participation per 42 CFR 1001',
      'SAM.gov exclusion database must also be checked per 105 CMR 155.000',
      'Monthly verification recommended by OIG',
      'MassHealth requires exclusion verification per M.G.L. c. 118E',
      'Penalties can exceed $10,000 per day for employing excluded individuals',
    ],
  },

  {
    id: 'MA-B4',
    itemId: 'B4',
    stateCode: 'MA',
    title: 'Professional License Verification',
    description: 'Verification of all professional licenses with Massachusetts licensing boards',
    severity: 'high',
    regulationCode: '42-484.80',
    regulationName: 'License Verification',
    agencyName: 'Massachusetts Board of Registration in Nursing',
    agencyAbbr: 'BORN',
    agencyPhone: '(617) 973-0800',
    agencyUrl: 'https://www.mass.gov/orgs/board-of-registration-in-nursing',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses with Massachusetts licensing boards before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you accept Nurse Licensure Compact (NLC) multistate licenses?',
        inputType: 'select',
        options: [
          { value: 'yes_verify', label: 'Yes, with verification of compact state license' },
          { value: 'ma_only', label: 'No, Massachusetts license required' },
          { value: 'unsure', label: 'Not sure of our policy' },
        ],
        helpText: 'Massachusetts joined the NLC in March 2024',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets requirements.',
      atRisk: 'License verification at hire only is insufficient. Implement ongoing verification and renewal tracking.',
      nonCompliant: 'No license verification process creates significant risk. Implement verification immediately.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      'Massachusetts joined the Nurse Licensure Compact (NLC) in March 2024',
      'NLC multistate licenses are valid in Massachusetts',
      'Verify licenses at mass.gov/orgs/board-of-registration-in-nursing',
      'RNs, LPNs, and APRNs must maintain current Massachusetts or NLC license',
    ],
  },

  {
    id: 'MA-B5',
    itemId: 'B5',
    stateCode: 'MA',
    title: 'Personnel File Documentation (42 CFR 484.80)',
    description: 'Required personnel file documentation for home health agency staff',
    severity: 'medium',
    regulationCode: '42-484.80',
    regulationName: 'Personnel',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following do ALL your employees have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'application', label: 'Employment application' },
          { value: 'photo_id', label: 'Government-issued photo ID copy' },
          { value: 'license', label: 'Professional license verification (if applicable)' },
          { value: 'job_description', label: 'Signed job description' },
          { value: 'hipaa', label: 'Signed HIPAA/confidentiality agreement' },
          { value: 'cori', label: 'CORI background check results' },
          { value: 'health_clearance', label: 'Health clearance/physical exam' },
          { value: 'tb', label: 'TB screening results' },
          { value: 'training', label: 'Training/competency documentation' },
          { value: 'performance', label: 'Performance evaluations' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel file documentation is complete.',
      atRisk: 'Some personnel files are missing required documentation. Audit all files for completeness.',
      nonCompliant: 'Personnel files are significantly incomplete. This will result in survey deficiencies.',
      unknown: 'Audit personnel files for required documentation.',
    },
    stateNotes: [
      'Personnel files must include CORI results per M.G.L. c. 6, Section 172',
      'Professional license verification required per 105 CMR 155.000',
      'TB screening documentation required per 42 CFR 484.70',
      'Competency verification required for all clinical staff per M.G.L. c.111',
      'Records must be retained per agency retention policy',
    ],
  },

  // ============================================
  // SECTION C: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'MA-C1',
    itemId: 'C1',
    stateCode: 'MA',
    title: 'Home Health Aide Training (42 CFR 484.80)',
    description: 'Home health aides must complete 75 hours of training per federal requirements',
    severity: 'high',
    regulationCode: '42-484.80',
    regulationName: 'HHA Training',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your Home Health Aides have documented completion of a 75-hour training program meeting 42 CFR 484.80 requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs have completed approved training' },
          { value: 'most', label: 'Most have completed training, some are pending' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
        helpText: 'HHAs must complete 75-hour training including at least 16 hours of supervised practical training',
      },
      {
        id: 'q2',
        question: 'Do HHAs complete annual in-service training of at least 12 hours?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do you complete competency evaluations for HHAs?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, at hire and annually' },
          { value: 'initial_only', label: 'At hire only' },
          { value: 'no', label: 'No formal evaluations' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'initial_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q3', value: 'initial_only' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA training program meets 42 CFR 484.80 requirements.',
      atRisk: 'HHA training program has gaps. Ensure all HHAs complete required training and annual competency evaluations.',
      nonCompliant: 'HHAs are providing care without proper training. This violates 42 CFR 484.80. Immediately verify training for all HHAs.',
      unknown: 'Verify HHA training documentation meets federal requirements.',
    },
    stateNotes: [
      '75 hours initial training required (minimum 16 hours supervised practical)',
      '12 hours annual in-service training required',
      'Annual competency evaluation required on 13 key subject areas',
      'Training must be provided by or under supervision of RN',
      'CNAs meeting federal requirements may work as HHAs',
    ],
  },

  {
    id: 'MA-C2',
    itemId: 'C2',
    stateCode: 'MA',
    title: 'Mandated Reporter Training - Child Abuse (M.G.L. c. 119, § 51A)',
    description: 'Massachusetts mandated reporter training required for child abuse recognition and reporting',
    severity: 'high',
    regulationCode: '119-51A',
    regulationName: 'Child Abuse Reporting',
    agencyName: 'Massachusetts Department of Children and Families',
    agencyAbbr: 'DCF',
    agencyPhone: '(800) 792-5200',
    agencyUrl: 'https://www.mass.gov/dcf',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff have documented 51A Mandated Reporter training for child abuse?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have completed 51A training' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not sure what 51A training is' },
        ],
        required: true,
        helpText: '51A training covers child abuse and neglect recognition and reporting requirements',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have completed 51A mandated reporter training per M.G.L. c. 119, § 51A.',
      atRisk: 'Some staff are missing 51A training. Ensure all staff complete mandated reporter training.',
      nonCompliant: '51A Mandated Reporter training is required for all staff. Failure to report suspected child abuse can result in fines up to $1,000 and criminal penalties. DCF Child Abuse Hotline: (800) 792-5200.',
      unknown: 'Verify all staff have completed 51A training per M.G.L. c. 119, § 51A.',
    },
    stateNotes: [
      'Health care workers are mandated reporters under M.G.L. c. 119, § 51A',
      'DCF Child Abuse Hotline: (800) 792-5200',
      'Willful failure to report resulting in serious injury can result in fine up to $5,000 and imprisonment',
      'Reporters acting in good faith have immunity from civil and criminal liability',
    ],
  },

  {
    id: 'MA-C3',
    itemId: 'C3',
    stateCode: 'MA',
    title: 'Mandated Reporter Training - Elder Abuse (M.G.L. c. 19A, § 15)',
    description: 'Massachusetts mandated reporter training required for elder abuse recognition and reporting',
    severity: 'high',
    regulationCode: '19A-15',
    regulationName: 'Elder Abuse Reporting',
    agencyName: 'Executive Office of Elder Affairs',
    agencyAbbr: 'EOEA',
    agencyPhone: '(800) 922-2275',
    agencyUrl: 'https://www.mass.gov/elder-abuse-neglect',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff have documented elder abuse recognition and reporting training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have completed training' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No elder abuse training program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report elder abuse to Protective Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have completed elder abuse recognition and reporting training per M.G.L. c. 19A, § 15.',
      atRisk: 'Some staff are missing elder abuse training. Ensure all staff know to report to Elder Abuse Hotline: (800) 922-2275.',
      nonCompliant: 'Elder abuse recognition and reporting training is required. Health care workers are mandated reporters. Elder Abuse Hotline: (800) 922-2275.',
      unknown: 'Verify all staff have completed elder abuse training per M.G.L. c. 19A, § 15.',
    },
    stateNotes: [
      'Health care workers are mandated reporters under M.G.L. c. 19A, § 15',
      'Elder Abuse Hotline: (800) 922-2275 (24/7)',
      'Oral report must be made immediately',
      'Written report required within 48 hours',
      'Reporters acting in good faith have immunity from liability',
    ],
  },

  {
    id: 'MA-C4',
    itemId: 'C4',
    stateCode: 'MA',
    title: 'TB Screening',
    description: 'TB screening required for all patient care staff',
    severity: 'high',
    regulationCode: '42-484.70',
    regulationName: 'TB Screening',
    agencyName: 'Massachusetts Department of Public Health',
    agencyAbbr: 'DPH',
    agencyPhone: '(617) 983-6800',
    agencyUrl: 'https://www.mass.gov/tuberculosis-tb-prevention-and-control-program',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your patient care staff have current TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have TB screening documentation' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
        helpText: 'TB screening must be completed before patient contact',
      },
      {
        id: 'q2',
        question: 'What TB screening method do you use?',
        inputType: 'select',
        options: [
          { value: 'tst', label: 'Tuberculin skin test (TST/PPD)' },
          { value: 'igra', label: 'Blood test (IGRA - QFT or T-SPOT)' },
          { value: 'either', label: 'Either method based on employee preference' },
          { value: 'unsure', label: 'Not sure' },
        ],
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT - Check for disqualifying conditions first
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT - All positive conditions met
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      // 3. AT_RISK - Partial compliance
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is complete for all staff.',
      atRisk: 'Some TB screenings may be missing. Review all staff files for documentation.',
      nonCompliant: 'Staff are working without TB screening documentation. This violates infection control requirements.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'Both TST and IGRA blood tests are acceptable',
      'Annual screening may be required based on risk assessment',
      'Document symptom screening for staff with prior positive tests',
      'DPH TB Program: (617) 983-6800',
    ],
  },

  {
    id: 'MA-C5',
    itemId: 'C5',
    stateCode: 'MA',
    title: 'HIPAA Training (45 CFR 164.530)',
    description: 'HIPAA requires documented privacy and security training',
    severity: 'high',
    regulationCode: '45-164.530',
    regulationName: 'HIPAA Training',
    agencyName: 'HHS Office for Civil Rights',
    agencyAbbr: 'OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees receive documented HIPAA training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees with documented completion' },
          { value: 'most', label: 'Most employees, some gaps exist' },
          { value: 'no', label: 'No formal HIPAA training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When do employees receive HIPAA training?',
        inputType: 'checklist',
        options: [
          { value: 'hire', label: 'At hire (within reasonable time)' },
          { value: 'annual', label: 'Annual refresher training' },
          { value: 'changes', label: 'When policies change' },
          { value: 'breach', label: 'Following any privacy incident' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA training is documented for all employees per 45 CFR 164.530.',
      atRisk: 'HIPAA training documentation has gaps. Complete training for all employees.',
      nonCompliant: 'HIPAA training is not being provided. This violates 45 CFR 164.530.',
      unknown: 'Verify HIPAA training documentation.',
    },
    stateNotes: [
      'HIPAA training required per 45 CFR 164.530(b) for all workforce members',
      'Training must occur within reasonable time after hire per 105 CMR 155.000',
      'Annual refresher training recommended per M.G.L. c.111',
      'Training must cover privacy, security, and breach notification',
      'Documentation of training completion required in personnel files',
    ],
  },

  {
    id: 'MA-C6',
    itemId: 'C6',
    stateCode: 'MA',
    title: 'Infection Control Training (42 CFR 484.70)',
    description: 'Infection prevention and control training required for all clinical staff',
    severity: 'high',
    regulationCode: '42-484.70',
    regulationName: 'Infection Control Training',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL clinical staff receive documented infection control training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff with documented training' },
          { value: 'most', label: 'Most staff, some gaps exist' },
          { value: 'no', label: 'No formal infection control training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which infection control topics are covered? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hand_hygiene', label: 'Hand hygiene' },
          { value: 'ppe', label: 'Personal protective equipment (PPE)' },
          { value: 'standard_precautions', label: 'Standard precautions' },
          { value: 'transmission', label: 'Transmission-based precautions' },
          { value: 'bloodborne', label: 'Bloodborne pathogen exposure' },
          { value: 'waste', label: 'Medical waste disposal' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control training is documented for all staff per 42 CFR 484.70.',
      atRisk: 'Infection control training has gaps. Review training curriculum and staff completion.',
      nonCompliant: 'Infection control training is not being provided. This violates 42 CFR 484.70.',
      unknown: 'Verify infection control training documentation.',
    },
    stateNotes: [
      'Infection control training required per 42 CFR 484.70 and 105 CMR 155.000',
      'Bloodborne pathogen training required per OSHA 29 CFR 1910.1030',
      'Annual refresher training recommended per M.G.L. c.111',
      'Training must cover hand hygiene, PPE, and standard precautions',
      'DPH TB Program available for consultation: (617) 983-6800',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'MA-D1',
    itemId: 'D1',
    stateCode: 'MA',
    title: 'Infection Control Program (42 CFR 484.70)',
    description: 'Medicare-certified agencies must maintain a comprehensive infection prevention and control program',
    severity: 'high',
    regulationCode: '42-484.70',
    regulationName: 'Infection Control',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Which infection control components does your agency have in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policy', label: 'Written infection control policies and procedures' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'ppe', label: 'PPE availability and training' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'waste', label: 'Medical waste disposal procedures' },
          { value: 'exposure', label: 'Exposure control plan (OSHA)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets 42 CFR 484.70 requirements.',
      atRisk: 'Infection control program is missing some required components.',
      nonCompliant: 'Infection control program is inadequate. Review 42 CFR 484.70 requirements.',
      unknown: 'Review infection control program against federal requirements.',
    },
    stateNotes: [
      'Written infection control program required per 42 CFR 484.70',
      'Program must include surveillance and reporting per 105 CMR 155.000',
      'Bloodborne pathogen exposure control plan required per OSHA per M.G.L. c.149, Section 6',
      'DPH communicable disease reporting requirements apply per M.G.L. c.111, Section 111',
      'Staff training on infection prevention required annually',
    ],
  },

  {
    id: 'MA-D2',
    itemId: 'D2',
    stateCode: 'MA',
    title: 'Hepatitis B Vaccination Offer (29 CFR 1910.1030)',
    description: 'OSHA requires Hepatitis B vaccination be offered to at-risk employees',
    severity: 'medium',
    regulationCode: '29-1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'OSHA',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to employees with potential bloodborne pathogen exposure?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, offered to all at-risk employees at no cost' },
          { value: 'some', label: 'Offered to some employees' },
          { value: 'no', label: 'No / not offered' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document employee acceptance or declination of the vaccine?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA 29 CFR 1910.1030 requirements.',
      atRisk: 'Hepatitis B vaccination program may be incomplete. Document all offers and declinations.',
      nonCompliant: 'Hepatitis B vaccination is not being offered. This violates 29 CFR 1910.1030.',
      unknown: 'Verify Hepatitis B vaccination procedures are in place.',
    },
    stateNotes: [
      'Hepatitis B vaccination must be offered at no cost per 29 CFR 1910.1030',
      'Declination form required if employee refuses per 105 CMR 155.000',
      'Post-exposure evaluation and follow-up required per M.G.L. c.149, Section 6',
      'Employee may accept vaccine at any time during employment',
      'Documentation must be maintained in personnel file',
    ],
  },

  {
    id: 'MA-D3',
    itemId: 'D3',
    stateCode: 'MA',
    title: 'Workers\' Compensation Coverage (M.G.L. c. 152)',
    description: 'Massachusetts requires workers\' compensation coverage for employees',
    severity: 'high',
    regulationCode: '152',
    regulationName: 'Workers Compensation',
    agencyName: 'Massachusetts Department of Industrial Accidents',
    agencyAbbr: 'DIA',
    agencyPhone: '(617) 727-4900',
    agencyUrl: 'https://www.mass.gov/orgs/department-of-industrial-accidents',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain current workers\' compensation insurance coverage?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When does your workers\' compensation policy expire?',
        inputType: 'date',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers\' compensation coverage is current per M.G.L. c. 152.',
      atRisk: 'Workers\' compensation policy expires soon. Renew immediately.',
      nonCompliant: 'Workers\' compensation coverage is missing or expired. This violates M.G.L. c. 152. Contact DIA at (617) 727-4900.',
      unknown: 'Verify workers\' compensation coverage is in place.',
    },
    stateNotes: [
      'All Massachusetts employers must carry workers\' compensation insurance',
      'Coverage required for all employees, including part-time',
      'DIA enforces workers\' compensation requirements',
      'Failure to maintain coverage can result in stop-work orders and penalties',
    ],
  },

  // ============================================
  // SECTION E: CARE DELIVERY
  // ============================================
  {
    id: 'MA-E1',
    itemId: 'E1',
    stateCode: 'MA',
    title: 'Patient Assessment (42 CFR 484.55)',
    description: 'Comprehensive patient assessments required before services begin',
    severity: 'high',
    regulationCode: '42-484.55',
    regulationName: 'Assessment',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete a comprehensive assessment for EVERY patient within required timeframes?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within required timeframes' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No formal assessment process' },
        ],
        required: true,
        helpText: 'Initial assessment must be completed within 48 hours of referral or within 48 hours of patient\'s return home',
      },
      {
        id: 'q2',
        question: 'Which assessment elements does your agency document? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physical', label: 'Physical health status' },
          { value: 'functional', label: 'Functional abilities and limitations' },
          { value: 'cognitive', label: 'Cognitive and mental status' },
          { value: 'psychosocial', label: 'Psychosocial status' },
          { value: 'medications', label: 'Medication review and reconciliation' },
          { value: 'nutrition', label: 'Nutritional status' },
          { value: 'home_safety', label: 'Home environment safety' },
          { value: 'caregiver', label: 'Caregiver availability and capability' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 7 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient assessments meet 42 CFR 484.55 requirements.',
      atRisk: 'Assessments may be incomplete or delayed. Review 42 CFR 484.55 for required elements and timeframes.',
      nonCompliant: 'No formal assessment process exists. This violates 42 CFR 484.55.',
      unknown: 'Review assessment process against federal requirements.',
    },
    stateNotes: [
      'Initial assessment within 48 hours of referral per 42 CFR 484.55',
      'Comprehensive assessment required per 105 CMR 155.000',
      'Assessment must be completed by qualified clinician per M.G.L. c.111',
      'OASIS data collection required for Medicare patients',
      'Reassessment required with significant change in condition',
    ],
  },

  {
    id: 'MA-E2',
    itemId: 'E2',
    stateCode: 'MA',
    title: 'Plan of Care (42 CFR 484.60)',
    description: 'Written plan of care required for each patient',
    severity: 'high',
    regulationCode: '42-484.60',
    regulationName: 'Plan of Care',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Patient diagnoses' },
          { value: 'services', label: 'Types of services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of visits' },
          { value: 'goals', label: 'Measurable patient-specific goals' },
          { value: 'medications', label: 'Medications and treatments' },
          { value: 'physician', label: 'Physician orders/certification' },
          { value: 'update', label: 'Plan review schedule (at least every 60 days)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet 42 CFR 484.60 requirements.',
      atRisk: 'Plans of care may be incomplete or created after services begin.',
      nonCompliant: 'Services are being provided without written plans of care. This violates 42 CFR 484.60.',
      unknown: 'Review plan of care process against federal requirements.',
    },
    stateNotes: [
      'Written plan of care required before services begin per 42 CFR 484.60',
      'Plan must be reviewed at least every 60 days per 105 CMR 155.000',
      'Physician orders/certification required per M.G.L. c.111',
      'Patient/family must participate in care planning',
      'Plan must include measurable patient-specific goals',
    ],
  },

  {
    id: 'MA-E3',
    itemId: 'E3',
    stateCode: 'MA',
    title: 'RN Supervision of Home Health Aides (42 CFR 484.80)',
    description: 'RN supervision required per federal regulations',
    severity: 'high',
    regulationCode: '42-484.80',
    regulationName: 'Supervision',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide RN supervision of home health aides as required?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, per regulatory schedule' },
          { value: 'partial', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No supervision program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does RN supervision occur?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'At least every 14 days' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'less', label: 'Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Does the RN make an on-site visit to the patient\'s home during supervision?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'every_14' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'equals', questionId: 'q2', value: 'every_30' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets 42 CFR 484.80(h) requirements.',
      atRisk: 'RN supervision may not meet required frequency or on-site visit requirements.',
      nonCompliant: 'No RN supervision program exists. This violates 42 CFR 484.80(h). Implement RN supervision immediately.',
      unknown: 'Review supervision requirements per 42 CFR 484.80(h).',
    },
    stateNotes: [
      'Supervision must occur at least every 14 days',
      'RN must make an on-site visit to patient\'s home',
      'Supervision visit must occur while HHA is providing care',
      'Alternative supervision timing if no HHA services for 14+ days',
    ],
  },

  {
    id: 'MA-E4',
    itemId: 'E4',
    stateCode: 'MA',
    title: 'Clinical Documentation',
    description: 'Clinical documentation requirements for home health services',
    severity: 'high',
    regulationCode: '42-484.110',
    regulationName: 'Clinical Records',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Which documentation elements are completed for EVERY visit? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'date_time', label: 'Date and time of visit' },
          { value: 'services', label: 'Services provided' },
          { value: 'patient_status', label: 'Patient status and observations' },
          { value: 'signature', label: 'Clinician signature' },
          { value: 'care_plan', label: 'Progress toward care plan goals' },
          { value: 'incidents', label: 'Incident documentation (when applicable)' },
          { value: 'communication', label: 'Communication with physician/team' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How quickly are visit notes completed after the visit?',
        inputType: 'select',
        options: [
          { value: 'same_day', label: 'Same day' },
          { value: 'within_48', label: 'Within 48 hours' },
          { value: 'within_week', label: 'Within one week' },
          { value: 'varies', label: 'Varies / no standard' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'includes', questionId: 'q2', value: ['same_day', 'within_48'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'within_week' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation meets 42 CFR 484.110 requirements.',
      atRisk: 'Documentation may be incomplete or delayed. Review documentation practices.',
      nonCompliant: 'Clinical documentation does not meet requirements. Implement comprehensive documentation procedures.',
      unknown: 'Review documentation practices against federal requirements.',
    },
    stateNotes: [
      'Clinical records required per 42 CFR 484.110',
      'Documentation must be completed timely per 105 CMR 155.000',
      'Records must be retained per M.G.L. c.111, Section 70 (minimum 7 years)',
      'All entries must be signed and dated',
      'Patient abuse incidents must be documented and reported per 105 CMR 155.000',
    ],
  },

  {
    id: 'MA-E5',
    itemId: 'E5',
    stateCode: 'MA',
    title: 'Patient Rights (42 CFR 484.50)',
    description: 'Patient rights notification and documentation requirements',
    severity: 'high',
    regulationCode: '42-484.50',
    regulationName: 'Patient Rights',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(617) 753-8000',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide EVERY patient with written notice of their rights before or at start of services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before or at first visit' },
          { value: 'usually', label: 'Usually, but not always documented' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your patient rights document include required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'dignity', label: 'Right to dignity and respect' },
          { value: 'participation', label: 'Right to participate in care planning' },
          { value: 'info', label: 'Right to information about services and charges' },
          { value: 'privacy', label: 'Right to privacy and confidentiality' },
          { value: 'complaint', label: 'Right to voice complaints/grievances' },
          { value: 'refuse', label: 'Right to refuse services' },
          { value: 'contact', label: 'State hotline contact information' },
          { value: 'advance_directive', label: 'Advance directive information' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 7 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights documentation meets 42 CFR 484.50 requirements.',
      atRisk: 'Patient rights documentation may be incomplete or not consistently provided.',
      nonCompliant: 'Patients are not receiving required rights information. This violates 42 CFR 484.50.',
      unknown: 'Review patient rights process against federal requirements.',
    },
    stateNotes: [
      'Include DPH Patient Abuse Hotline: (800) 462-5540',
      'Include information on how to file complaints with DPH',
      'Document patient/representative acknowledgment of receipt',
      'Provide in language patient understands',
    ],
  },

  {
    id: 'MA-E6',
    itemId: 'E6',
    stateCode: 'MA',
    title: 'Notice of Privacy Practices (45 CFR 164.520)',
    description: 'HIPAA requires Notice of Privacy Practices to be provided to patients',
    severity: 'high',
    regulationCode: '45-164.520',
    regulationName: 'NPP',
    agencyName: 'HHS Office for Civil Rights',
    agencyAbbr: 'OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide a Notice of Privacy Practices to all patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do patients sign acknowledgment of receiving the Notice of Privacy Practices?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Notice of Privacy Practices is provided and documented per 45 CFR 164.520.',
      atRisk: 'NPP is provided but acknowledgment documentation may be incomplete.',
      nonCompliant: 'Notice of Privacy Practices is not being provided. This violates 45 CFR 164.520.',
      unknown: 'Verify Notice of Privacy Practices procedures.',
    },
    stateNotes: [
      'Notice of Privacy Practices required per 45 CFR 164.520',
      'Must be provided at first service delivery per 105 CMR 155.000',
      'Patient acknowledgment of receipt must be documented per M.G.L. c.111, Section 70E',
      'NPP must be updated when privacy practices change materially',
      'Provide in language patient understands',
    ],
  },
];

export default MASSACHUSETTS_QUESTIONS;